import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import Select from '../../../components/shared/Select/Select';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import { useAuth } from '../../../context/AuthContext';
import {
  adminResetPassword,
  changeRole,
  createUser,
  deleteUser,
  toggleVerified,
  updateUser,
} from '../../../services/accounts';
import { listStops } from '../../../services/stops';
import type { Role, User, Stop } from '../../../types/api';


const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  transport_manager: 'Transport manager',
  accountant: 'Accountant',
  partner: 'Partner',
  driver: 'Driver',
  conductor: 'Conductor',
  clerk: 'Clerk',
  passenger: 'Passenger',
};

interface FormState {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  id_number: string;
  role: Role;
  station: string;
  password: string;
}

const empty: FormState = {
  username: '', first_name: '', last_name: '',
  email: '', phone_number: '', id_number: '',
  role: 'passenger', station: '', password: '',
};

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { data: users, loading, refetch } = usePaginatedResource<User>('/auth/users/');

  const [editing, setEditing] = useState<User | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [roleTarget, setRoleTarget] = useState<User | null>(null);
  const [roleValue, setRoleValue] = useState<Role>('passenger');
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetPassword, setResetPasswordValue] = useState('');

  // Stops for the clerk station picker.
  const [stops, setStops] = useState<Stop[]>([]);
  useEffect(() => { listStops(false).then(setStops).catch(() => setStops([])); }, []);
  const stationOptions = useMemo(
    () => [
      { value: '', label: 'No station' },
      ...stops.map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [stops],
  );

  const save = useResourceMutation(
    async (state: FormState) => {
      const station = state.station ? parseInt(state.station, 10) : null;
      const { station: _st, password: _pw, ...base } = state;
      if (editing) {
        return updateUser(editing.id, { ...base, station });
      }
      return createUser({ ...base, station, password: state.password });
    },
    { successMessage: editing ? 'User updated' : 'User created' }
  );

  const del = useResourceMutation(
    (id: number) => deleteUser(id),
    { successMessage: 'User deleted' }
  );

  const roleM = useResourceMutation(
    ({ id, role }: { id: number; role: Role }) => changeRole(id, role),
    { successMessage: 'Role updated' }
  );

  const verifyM = useResourceMutation(
    (id: number) => toggleVerified(id),
    { successMessage: 'Verification toggled' }
  );

  const resetM = useResourceMutation(
    ({ id, password }: { id: number; password: string }) => adminResetPassword(id, password),
    { successMessage: 'Password reset' }
  );

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };

  const openEdit = (u: User) => {
    setEditing(u);
    setForm({
      username: u.username,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      phone_number: u.phone_number,
      id_number: u.id_number ?? '',
      role: u.role,
      station: u.station ? String(u.station) : '',
      password: '',
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.run(form);
      setShowForm(false);
      setForm(empty);
      setEditing(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.run(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleRoleChange = async () => {
    if (!roleTarget) return;
    try {
      await roleM.run({ id: roleTarget.id, role: roleValue });
      setRoleTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleVerifyToggle = async (u: User) => {
    try {
      await verifyM.run(u.id);
      refetch();
    } catch { /* toast */ }
  };

  const handleReset = async () => {
    if (!resetTarget || !resetPassword) return;
    try {
      await resetM.run({ id: resetTarget.id, password: resetPassword });
      setResetTarget(null);
      setResetPasswordValue('');
    } catch { /* toast */ }
  };

  const columns: Column<User>[] = [
    { key: 'username', header: 'Username', sortable: true },
    { key: 'first_name', header: 'Name',
      render: (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—' },
    { key: 'email', header: 'Email' },
    { key: 'phone_number', header: 'Phone' },
    { key: 'role', header: 'Role', filterable: true,
      filterOptions: Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label })),
      render: (u) => ROLE_LABEL[u.role] ?? u.role },
    { key: 'is_verified', header: 'Verified', filterable: true, filterOptions: [
      { value: 'true', label: 'Verified' },
      { value: 'false', label: 'Unverified' },
    ], render: (u) => (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        u.is_verified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${u.is_verified ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden />
        {u.is_verified ? 'Verified' : 'Unverified'}
      </span>
    ) },
  ];

  const actions = (u: User) => {
    const isSelf = currentUser?.id === u.id;
    return (
      <div className="inline-flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>Edit</Button>
        <Button variant="secondary" size="sm" onClick={() => {
          setRoleTarget(u);
          setRoleValue(u.role);
        }}>Role</Button>
        <Button variant="ghost" size="sm" onClick={() => handleVerifyToggle(u)}
                loading={verifyM.loading}>
          {u.is_verified ? 'Unverify' : 'Verify'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => {
          setResetTarget(u);
          setResetPasswordValue('');
        }}>Reset password</Button>
        {!isSelf && (
          <Button variant="danger" size="sm" onClick={() => setDeleteTarget(u)}>Delete</Button>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="All platform users. Manage roles, verification, and passwords."
        actions={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New user
          </Button>
        }
      />

      <DataTable<User>
        columns={columns}
        data={users}
        rowKey="id"
        loading={loading}
        emptyTitle="No users yet"
        emptyDescription="Add team members to start managing the fleet together."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New user
          </Button>
        }
        actions={actions}
      />

      {/* Create / Edit modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit user' : 'New user'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
            <Button onClick={handleSave} loading={save.loading}>{editing ? 'Save changes' : 'Create user'}</Button>
          </>
        }
      >
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Username" name="username" required error={save.fieldErrors.username}>
            <Input id="username" required value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={!!editing} />
          </FormField>
          <FormField label="Email" name="email" required error={save.fieldErrors.email}>
            <Input id="email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </FormField>
          <FormField label="First name" name="first_name" required error={save.fieldErrors.first_name}>
            <Input id="first_name" required value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </FormField>
          <FormField label="Last name" name="last_name" required error={save.fieldErrors.last_name}>
            <Input id="last_name" required value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </FormField>
          <FormField label="Phone" name="phone_number" required error={save.fieldErrors.phone_number}>
            <Input id="phone_number" type="tel" required value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
          </FormField>
          <FormField label="ID number" name="id_number" error={save.fieldErrors.id_number}>
            <Input id="id_number" value={form.id_number}
              onChange={(e) => setForm({ ...form, id_number: e.target.value })} />
          </FormField>
          <FormField label="Role" name="role" required error={save.fieldErrors.role}>
            <Select
              id="role" name="role" value={form.role}
              onChange={(v) => setForm({ ...form, role: v as Role })}
              options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
              fullWidth
            />
          </FormField>
          {form.role === 'clerk' && (
            <FormField label="Station" name="station"
                       hint="Destination office — scopes which parcels this clerk sees"
                       error={save.fieldErrors.station}>
              <SearchableSelect
                id="station"
                options={stationOptions}
                value={form.station}
                onChange={(v) => setForm({ ...form, station: v })}
                placeholder="Select station…"
                searchPlaceholder="Search stops…"
                aria-label="Station"
              />
            </FormField>
          )}
          {!editing && (
            <FormField label="Initial password" name="password" required error={save.fieldErrors.password}
                       hint="8+ characters">
              <Input id="password" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </FormField>
          )}
        </form>
      </Modal>

      {/* Role change modal */}
      <Modal
        isOpen={!!roleTarget}
        onClose={() => setRoleTarget(null)}
        title="Change role"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoleTarget(null)} disabled={roleM.loading}>Cancel</Button>
            <Button variant="primary" onClick={handleRoleChange} loading={roleM.loading}>Save role</Button>
          </>
        }
      >
        {roleTarget && (
          <div className="space-y-3">
            <p className="text-base text-slate-700">Change role for <strong>{roleTarget.username}</strong>:</p>
            <Select
              value={roleValue}
              onChange={(v) => setRoleValue(v as Role)}
              options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
              fullWidth
            />
          </div>
        )}
      </Modal>

      {/* Admin reset password modal */}
      <Modal
        isOpen={!!resetTarget}
        onClose={() => { setResetTarget(null); setResetPasswordValue(''); }}
        title="Reset password"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setResetTarget(null); setResetPasswordValue(''); }}
                    disabled={resetM.loading}>Cancel</Button>
            <Button variant="danger" onClick={handleReset} loading={resetM.loading}>Reset</Button>
          </>
        }
      >
        {resetTarget && (
          <FormField label="New password" name="new_password" required hint="8+ characters"
                     error={resetM.fieldErrors.new_password}>
            <Input id="new_password" type="password" required value={resetPassword}
              onChange={(e) => setResetPasswordValue(e.target.value)} />
          </FormField>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete user?"
        message={`Delete ${deleteTarget?.username ?? ''}? This cannot be undone and may affect related records.`}
        confirmLabel="Delete"
        variant="danger"
        loading={del.loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default Users;
